import { RadioGroup } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import React, { useId } from "react";
import { cn } from "../utils";
import { SegmentedControlItem } from "./segmented-control-item";

/** One selectable entry. `description` and `action` render in the vertical orientation only. */
export interface SegmentedControlItemInput<U extends string, T extends string> {
	value: T;
	label: U;
	cyId: string;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
	/** A muted line beneath the label. Vertical orientation only — horizontal has no room for it. */
	description?: string;
	/**
	 * Interactive trailing content (a row menu, a button), rendered outside the radio's label and
	 * shielded from its click, so using it does not also change the selection.
	 */
	action?: React.ReactNode;
	/** Shown but not selectable — an option that exists and is out of reach right now. */
	isDisabled?: boolean;
}

/** Props Interface */
interface SegmentedControlProps<U extends string, T extends string>
	extends VariantProps<typeof segmentedControl> {
	value: T;
	onChange: (value: T) => void;
	items: Array<SegmentedControlItemInput<U, T> | "divider">;
}

export function SegmentedControl<U extends string, T extends string>({
	value,
	onChange,
	items,
	orientation = "horizontal",
}: SegmentedControlProps<U, T>) {
	/** Classes */
	const segmentedControlClasses = segmentedControl({
		orientation,
	});

	/** Hooks */
	const parentId = useId();

	/** Render */
	return (
		<RadioGroup
			value={value}
			onChange={(value) => onChange(value)}
			className={cn(segmentedControlClasses)}
		>
			{items.map((item, index) =>
				item === "divider" ? (
					<div
						key={`divider-${index}`}
						className={cn(
							"bg-primary-100 dark:bg-primary-800 shrink-0",
							orientation === "vertical" ? "h-px w-full my-1" : "w-px self-stretch mx-1",
						)}
					/>
				) : (
					<SegmentedControlItem
						key={item.value}
						parentId={parentId}
						orientation={orientation}
						{...item}
					/>
				),
			)}
		</RadioGroup>
	);
}

const segmentedControl = cva(
	["bg-primary-50 dark:bg-primary-900/70 border-[0.5px] border-color p-0.5"],
	{
		variants: {
			orientation: {
				horizontal: ["h-stack gap-0 w-max rounded-base"],
				vertical: ["v-stack gap-0 w-full rounded-base"],
			},
		},
		defaultVariants: {
			orientation: "horizontal",
		},
	},
);
